import { DEFAULT_LOCALE, LocaleCode } from '@choco-admin/types';
import { isValidEmail } from '@choco-admin/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { parseEmailList } from '../../../utils/buildIntegrationPayload';
import { isValidTimezone } from '../../../utils/timezones';
import { SupplierFormData } from '../types';

const validateRequiredString = (value: string | undefined | null): boolean =>
  Boolean(value?.trim());

/**
 * A stored time zone, which may be absent.
 *
 * Empty passes: a distributor created before the picker existed has none, and the
 * backend reads that as America/Los_Angeles. A non-empty value still has to be a
 * real zone, because the backend rejects anything else and a stored bad value
 * breaks delivery-date calculation.
 */
const TIMEZONE_FIELD = yup
  .string()
  .optional()
  .default('')
  .test(
    'timezoneIsRecognised',
    'Pick a time zone from the list',
    (value) => !value || isValidTimezone(value)
  );

/**
 * The same field on create, where it is required.
 *
 * An unset zone costs more than the Pacific fallback the picker's empty state
 * describes: order notification emails drop the order date rather than guess at
 * it, and `currencyFromTimezone` returns nothing for a product priced without a
 * currency.
 */
const REQUIRED_TIMEZONE_FIELD = TIMEZONE_FIELD.required('Pick a time zone');

/**
 * A comma-separated list of notification addresses.
 *
 * Nothing downstream checks these. `supplierService` validates them as
 * `Joi.string()` and maps them through unchanged, so a typo is stored and the
 * order worker later hands it to `sendEmail`: the distributor's order mail has no
 * valid destination and nothing says so. Legacy validated every address in the
 * list, and this restores that.
 *
 * Split with `parseEmailList`, which is what `buildIntegrationPayload` sends, so
 * what is validated and what is stored cannot drift. Empty passes, because an
 * empty list is how a distributor opts out.
 */
const EMAIL_LIST_FIELD = yup
  .string()
  .optional()
  .default('')
  .test(
    'everyAddressIsAnEmail',
    'Enter valid email addresses, separated by commas.',
    (value) => parseEmailList(value ?? '').every(isValidEmail)
  );

export const supplierFormValidationSchema = yup.object().shape({
  name: yup
    .string()
    .test('nameRequired', 'Company name is required', validateRequiredString)
    .required('Company name is required'),
  code: yup
    .string()
    .optional()
    .default('')
    .max(255, 'Use 255 characters or fewer.'),
  externalName: yup
    .string()
    .optional()
    .default('')
    .max(255, 'Use 255 characters or fewer.'),
  description: yup.string().optional(),
  primaryCategory: yup
    .string()
    .test(
      'primaryCategoryRequired',
      'Primary category is required',
      validateRequiredString
    )
    .required('Primary category is required'),
  secondaryCategories: yup.array().of(yup.string()).default([]),
  deliveryFee: yup.string().optional(),
  minimumOrderValue: yup.string().optional(),
  minimumOrderCurrency: yup.string().optional().default('EUR'),

  // Order preferences (toggles are always present)
  allowCustomProducts: yup.boolean().required().default(false),
  mandatoryCustomerNumber: yup.boolean().required().default(false),
  mandatoryDeliveryDate: yup.boolean().required().default(true),
  enableProductComments: yup.boolean().required().default(false),
  allowFractionalUnits: yup.boolean().required().default(false),
  allowOnlyOneVariant: yup.boolean().required().default(false),
  sortBy: yup
    .string()
    .oneOf(['alphabetical', 'orderGuide'] as const)
    .required()
    .default('orderGuide'),
  cutoffType: yup
    .string()
    .oneOf(['strict', 'flexible', 'no_cutoff'] as const)
    .required()
    .default('strict'),

  // Delivery days/times are only rendered in edit mode (DeliverySettingsSection
  // requires a saved supplier id). Skip the at-least-one-day requirement here
  // so create can succeed with empty defaults.
  deliveryDays: yup.mixed().optional().default({}),
  cutoffTimes: yup.mixed().optional().default({}),
  cutoffDays: yup.mixed().optional().default({}),

  // Settings tab fields
  isIntegrated: yup.boolean().optional().default(false),
  enablePromotions: yup.boolean().optional().default(false),
  enableTaxes: yup.boolean().optional().default(false),
  pricingDateSource: yup
    .mixed<'ORDER_DATE' | 'DELIVERY_DATE'>()
    .oneOf(['ORDER_DATE', 'DELIVERY_DATE'])
    .optional()
    .default('ORDER_DATE'),
  calculationPrecision: yup
    .number()
    .integer('Use a whole number of decimal places.')
    .min(0, 'Use 0 or more decimal places.')
    .max(10, 'Use 10 or fewer decimal places.')
    .optional(),
  referenceId: yup.string().optional().default(''),
  locale: yup
    .mixed<LocaleCode>()
    .oneOf(Object.values(LocaleCode))
    .required('Language is required')
    .default(DEFAULT_LOCALE),
  // Empty is allowed (the field is optional), but a non-empty value has to be a
  // real zone: the backend rejects anything else, and a stored bad value breaks
  // delivery-date calculation.
  // Optional here, required on create: see `supplierFormResolver`. An existing
  // distributor can be stored without one, so requiring it on edit would block
  // unrelated work on a field the reader did not come for.
  timezone: TIMEZONE_FIELD,

  // Ordering and eCommerce (POR-31)
  allowInvoiceOrderGuides: yup.boolean().optional().default(false),
  disableCustomerNumberEdits: yup.boolean().optional().default(false),
  restrictSalesRepDeliverySettingEdits: yup.boolean().optional().default(false),
  disableOrderedProductMerging: yup.boolean().optional().default(false),
  enableEcommerceProductGrid: yup.boolean().optional().default(false),
  allowEcommercePoNumber: yup.boolean().optional().default(false),
  allowCustomerAcquisition: yup.boolean().optional().default(false),

  // Notification fields
  notificationOrderEmails: EMAIL_LIST_FIELD,
  notificationSystemAlertEmails: EMAIL_LIST_FIELD,
  notificationComplaintEmails: EMAIL_LIST_FIELD,
  notificationWhatsappNumber: yup.string().optional().default(''),
  notificationSmsNumber: yup.string().optional().default(''),
  notificationOrderPdf: yup.boolean().optional().default(false),
  notificationOrderXlsx: yup.boolean().optional().default(false),

  // Catalog fields
  supplierDefaultSubCatalog: yup.string().optional().default(''),
  displayInMarketplace: yup.boolean().optional().default(false),
  displayToCustomers: yup.boolean().optional().default(false),
  supplierHideMyPrice: yup.boolean().optional().default(false),
  showOutOfStockProducts: yup.boolean().optional().default(false),
}) as unknown as yup.ObjectSchema<SupplierFormData>;

/**
 * The schema for a given form mode.
 *
 * Only `timezone` differs, and only on create. Edit keeps it optional so an
 * existing distributor stored without one stays editable.
 *
 * Order emails are deliberately not required. A distributor whose buyers order
 * through individual sales reps has no central address, which is normal in Spain,
 * and those orders still reach the reps: `handleOrderEmailNotif` reads each user's
 * own `orderPreference` alongside the supplier's.
 */
export const buildSupplierFormValidationSchema = (mode: 'create' | 'edit') =>
  mode === 'create'
    ? (supplierFormValidationSchema.shape({
        timezone: REQUIRED_TIMEZONE_FIELD,
      }) as unknown as yup.ObjectSchema<SupplierFormData>)
    : supplierFormValidationSchema;

export const supplierFormResolver = (mode: 'create' | 'edit') =>
  yupResolver<SupplierFormData>(buildSupplierFormValidationSchema(mode));
